import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PricingBadge } from '@/components/opportunities/OppBadges';
import { Loader, EmptyState } from '@/components/common/Feedback';
import api from '@/lib/api';
import { fmtMoney, CO_STATUSES } from '@/lib/format';

export default function ChangeOrders() {
  const navigate = useNavigate();
  const [cos, setCos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');

  useEffect(() => {
    setLoading(true);
    const params = status === 'all' ? {} : { status };
    api.get('/change-orders', { params }).then((r) => setCos(r.data)).finally(() => setLoading(false));
  }, [status]);

  const pricedTotal = cos.reduce((s, c) => s + (typeof c.total === 'number' ? c.total : 0), 0);
  const pricedCount = cos.filter((c) => typeof c.total === 'number').length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">{cos.length} change orders &middot; <span className="font-mono font-semibold text-slate-700">{fmtMoney(pricedTotal)}</span> across {pricedCount} fully-priced</p>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 h-9" data-testid="co-status-filter"><SelectValue placeholder="Filter status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {CO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? <Loader /> : cos.length === 0 ? <EmptyState icon={FileText} title="No change orders" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">CO #</th>
                  <th className="text-left px-4 py-2.5">Title</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Project</th>
                  <th className="text-left px-4 py-2.5 hidden lg:table-cell">Trade</th>
                  <th className="text-center px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cos.map((c) => (
                  <tr key={c.id} className="row-hover cursor-pointer" onClick={() => navigate(`/change-orders/${c.id}`)} data-testid={`co-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-slate-700">{c.co_number}</td>
                    <td className="px-4 py-3 text-slate-800 font-medium">{c.title}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.project_name}</td>
                    <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{c.trade}</td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {typeof c.total === 'number'
                        ? <span className="font-mono font-semibold">{fmtMoney(c.total)}</span>
                        : <PricingBadge status={c.pricing_status || 'unquantified'} testid={`co-list-pricing-${c.id}`} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
